import React, { Suspense } from 'react';

import { Box, GDSBoxProps, GDSStyleObject } from '@leagueplatform/genesis-core';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import {
  ErrorState,
  LoadingIndicator,
} from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { useMatch } from 'react-router-dom';
import { AppNavBarComponent } from 'components/header-nav/header-nav.component';
import { AppFooter } from 'components/app-footer/app-footer.component';
import { QueryParamModal } from 'components/modals/query-param-modal.component';
import { BookAppointmentModalHost } from 'components/modals/book-appointment-modal-host.component';

type BasePageProps = GDSBoxProps<'div'> & {
  css?: GDSStyleObject;
  isPrivatePage?: boolean;
};

/**
 * Returns true when the current URL matches a health-journey activity toolbox
 * route. The footer is hidden on these "immersive" screens so the toolbox UI
 * can use the full viewport.
 *
 * Covers both activity entry shapes registered by `@leagueplatform/health-journey`:
 *   - /<healthJourney>/activity/:userActivityId/toolbox
 *   - /<healthJourney>/campaign/:campaignId/activity/:activityId/toolbox
 *
 * Trailing `/*` is intentional so any nested toolbox sub-routes also match.
 */
const useIsOnActivityToolboxRoute = () => {
  const healthJourneyRoot = getModulePath(LEAGUE_MODULE_NAMES.healthJourney);
  const activityToolboxMatch = useMatch(
    `${healthJourneyRoot}/activity/:userActivityId/toolbox/*`,
  );
  const campaignActivityToolboxMatch = useMatch(
    `${healthJourneyRoot}/campaign/:campaignId/activity/:activityId/toolbox/*`,
  );
  return Boolean(activityToolboxMatch || campaignActivityToolboxMatch);
};

export const BasePage: React.FC<BasePageProps> = ({
  children,
  isPrivatePage = true,
  css,
  ...boxProps
}) => {
  useScrollTopOnLocationChange();
  const { formatMessage } = useIntl();
  const hideFooter = useIsOnActivityToolboxRoute();
  const showFooter = isPrivatePage && !hideFooter;

  return (
    <Box
      css={{
        display: 'grid',
        gridTemplateRows: isPrivatePage ? 'auto 1fr auto' : '1fr',
        minHeight: '100svh',
      }}
    >
      {isPrivatePage && <AppNavBarComponent />}
      <Box
        as="main"
        id="main-content"
        css={{
          isolation: 'isolate',
          position: 'relative',
          overflowX: 'hidden',
          backgroundColor: '$surfaceBackgroundPrimary',
          ...css,
        }}
        {...boxProps}
      >
        <Suspense fallback={<LoadingIndicator />}>
          <ObservabilityErrorBoundary
            fallback={
              <ErrorState
                headingString={formatMessage({
                  id: 'STR_ERROR_STATE_HEADING_GENERIC',
                })}
                bodyString={formatMessage({ id: 'STR_ERROR_STATE_BODY_API' })}
                buttonText={formatMessage({ id: 'STR_RELOAD' })}
                error={null}
              />
            }
            errorContext={{
              context: {
                Context: { location: 'Base Page ErrorBoundary' },
              },
            }}
          >
            {isPrivatePage && <QueryParamModal />}
            {isPrivatePage && <BookAppointmentModalHost />}
            {children}
          </ObservabilityErrorBoundary>
        </Suspense>
      </Box>
      {showFooter && <AppFooter />}
    </Box>
  );
};
